import store from '../../../store';
import { i18n } from '@/plugins/i18next'

export default class Utils {

  static async sleep(milliseconds) {
    return new Promise(resolve => setTimeout(resolve, milliseconds))
  }

  /**
   * Turns the given base64 encoded string into a blob.
   * Implementation taken form: https://stackoverflow.com/a/20151856
   *
   * @param base64Data data to encode
   * @param contentType content type of the blob
   * @returns {Blob}
   */
  static base64toBlob(base64Data, contentType) {
    contentType = contentType || '';
    var sliceSize = 1024;
    var byteCharacters = atob(base64Data);
    var bytesLength = byteCharacters.length;
    var slicesCount = Math.ceil(bytesLength / sliceSize);
    var byteArrays = new Array(slicesCount);

    for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      var begin = sliceIndex * sliceSize;
      var end = Math.min(begin + sliceSize, bytesLength);

      var bytes = new Array(end - begin);
      for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, {type: contentType});
  }

  /**
   * Extracts the file extension from the filename
   * Implementation taken form: https://stackoverflow.com/questions/680929/how-to-extract-extension-from-filename-string-in-javascript
   *
   * @param filename string to extract extension from
   * @returns {String}
   */
  static getFileExtension(filename) {
    var re = /(?:\.([^.]+))?$/;
    var ext = re.exec(filename);

    return ext[0];
  }

  /**
   * Extracts the file extension from the MIME type.
   *
   * @param MIME contains file extension to extract
   * @returns {string}
   */
  static getFileExtensionFromMIME(MIME){
    var slashIndex = MIME.indexOf('/');
    if(slashIndex === -1){
      return "";
    }
    return MIME.substring(slashIndex + 1);
  }

  /**
   * Shows snackbar
   * Implementation taken from: https://dev.to/stephannv/how-to-create-a-global-snackbar-using-nuxt-vuetify-and-vuex-1bda
   *
   * @param text string to be displayed in snackbar
   * @param snackbarColor color of snackbar
   * @param snackbarBtnColor color of close btn of snackbar
   * @param icon icon to be shown
   */
  static showSnackbar(icon, text, snackbarColor, snackbarBtnColor) {
    store.commit('snackbar/showMessage', {
      icon: icon,
      content: text,
      snackbarColor: snackbarColor,
      snackbarBtnColor: snackbarBtnColor
    });
  }

  /**
   * Shows successfull snackbar with green color
   *
   * @param text string to be displayed in snackbar
   */
  static showSuccessSnackbar(text) {
    store.commit('snackbar/showMessage', {
      icon: 'check_circle_outline',
      content: text,
      snackbarColor: 'light-green',
      snackbarBtnColor: 'white'
    });
  }

  /**
   * Shows warning snackbar with orange color
   *
   * @param text string to be displayed in snackbar
   */
  static showWarningSnackbar(text) {
    store.commit('snackbar/showMessage', {
      icon: 'warning',
      content: text,
      snackbarColor: 'orange',
      snackbarBtnColor: 'white'
    })
  }

  /**
   * Shows failure snackbar with red color
   *
   * @param text string to be displayed in snackbar
   */
  static showFailureSnackbar(text) {
    store.commit('snackbar/showMessage', {
      icon: 'error_outline',
      content: text,
      snackbarColor: 'red darken-2',
      snackbarBtnColor: 'white'
    });
  }

  /**
   * lowers and optionally formats given string and returns it
   * used to search in translation files
   *
   * @param {String} name string to format e.g. "AUSTRIA" or "Upper Austria"
   * @returns {String} e.g. "austria" or "upper-austria"
   */
  static lowerAndFormatName(name) {
    name = name.toLowerCase();
    if(name.indexOf(' ') >= 0) {
      name = name.split(' ').join('-');
    }

    return name;
  }

  /**
   * Translates given country name and returns translated
   *
   * @param {String} countryName name of country e.g. "AUSTRIA"
   * @returns {String} translated name e.g. "Österreich"
   */
  static translateCountry(countryName) {
    let lowerCountryName = this.lowerAndFormatName(countryName)

    let retval = i18n.i18next.exists('country:' + lowerCountryName + '.country');
    if (retval === true) {
       return i18n.i18next.t('country:' + lowerCountryName + '.country');
    } else {
      return countryName;
    }
  }

  /**
   * Automatically translates all countries in a given array and returns it
   *
   * @param countries array of countries to translate
   * @returns translated countries
   */
  static translateAllCountries(countries){
    for(var i = 0; i < countries.length; i++) {
      countries[i].translatedName = Utils.translateCountry(countries[i].name)
    }
    return countries;
  }

  /**
   * Translates given province name and returns translated
   *
   * @param {String} countryName name of country e.g. "AUSTRIA"
   * @param {String} name name of province e.g. "Upper Austria"
   * @returns {String} translated province name e.g. "Oberösterreich"
   */
  static translateProvinces(countryName, name) {
    var provinces;
    countryName = this.lowerAndFormatName(countryName)
    provinces =  i18n.i18next.t('country:' + countryName + '.provinces', { returnObjects: true})

    var originalName = name;
    name = this.lowerAndFormatName(name);

    let province = provinces[name];
    if (province === undefined)
      return originalName;

    return province;
  }

  /**
   * Translates all provinces of a specific selected country.
   *
   * @param countries contains all existing country information
   * @param provinces contains all existing provinces
   * @param selectedCountry country to translate provinces for
   * @returns translated provinces
   */
  static translateAllProvinces(countries, provinces, selectedCountry){
    for(var i = 0; i < countries.length; i++) {
      if(countries[i].id === selectedCountry) {
        for(var j = 0; j < provinces.length; j++) {
          provinces[j].translatedName = this.translateProvinces(countries[i].name, provinces[j].name)
        }
      }
    }
    return provinces;
  }

  /**
   * Will crop the text if acertain length has been reached and just set "..." at the end.
   *
   * @param {String} text text to crop
   * @param {Number} length max length of text until we crop, in characters
   * @param {Boolean} useWordBoundary if word should be cut off or should be returned fully
   * @returns {string|*}
   */
  static cropText(text, length, useWordBoundary){
    if(!text || ! length){
      return text;
    }
    if(text.length > length && !useWordBoundary){
      return text.substring(0, length) + " ...";
    } else if(text.length > length && useWordBoundary) {
      const subString = text.substring(0, length);
      return subString.substr(0, subString.lastIndexOf(" ")) + " ...";
    }
    return text;
  }

  /**
   * Sorts a given array by sortDesc and sortBy
   *
   * @param data array to sort
   * @param sortDesc if sorting should be ascending or descending
   * @param sortBy column to sort by
   * @returns {*} sorted array
   */
  static sort(data, sortDesc, sortBy) {
    if (!sortDesc) {
      data.sort((a, b) => {
        if(a[sortBy] < b[sortBy]) {
          return -1;
        }
        if(a[sortBy] > b[sortBy]) {
          return 1;
        }
        return 0;
      })
    } else {
      data.sort((a, b) => {
        if(a[sortBy] > b[sortBy]) {
          return -1;
        }
        if(a[sortBy] < b[sortBy]) {
          return 1;
        }
        return 0;
      })
    }

    return data;
  }

  /**
   * compares de-local date strings i.e. format 'dd.MM.yyyy'
   * ( f.e. '31.02.2022')
   * used as a compare-function for sort in DataTable-Headers
   *
   * @param a first date to compare
   * @param b second date to compare
   * @returns returns -1 if a &lt; b <br/>1 if a &gt; b and 0 if a==b or an exception was raised
   * because format was not valid
   */
  static compareLocalDeDateString(a,b) {
    // handle null values
    if (a === null && b === null) {
      return 0;
    }
    else if (a === null) {
      return -1;
    }
    else if ( b === null) {
      return 1;
    }

    let result = 0;
    try {
      let aArray = a.split('.');
      let aYearMonthDay = aArray[2] + '-' + aArray[1]+ '-' + aArray[0];
      let bArray = b.split('.');
      let bYearMonthDay = bArray[2] + '-' + bArray[1]+ '-' + bArray[0];
      result = aYearMonthDay === bYearMonthDay ? 0 : aYearMonthDay < bYearMonthDay ? -1 : 1;
    } catch (e) {
      result = 0;
    }
    return result;
  }

  /**
   * compare function for booleans who also sort null and undefined
   * order true - false - null - undefined - {other value}
   * @param a first boolean value
   * @param b second boolean value
   * @returns {number} returns -1 if a &lt; b <br/>1 if a &gt; b and <br/>0 if a==b
   */
  static compareBooleanWithNullUndefined(a,b) {
    let aVal = a === true ? 4 : a === false ? 3 : a === null ? 2 : a === undefined ? 1 : 0 ;
    let bVal = b === true ? 4 : b === false ? 3 : b === null ? 2 : b === undefined ? 1 : 0 ;
    return aVal === bVal ? 0 : aVal < bVal ? -1 : 1;
  }

  static generateUUIDv4() {
    return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
  }

  /**
   *  Round the number according to the accurate number
   *
   * @param number
   * @param accurate
   * @returns {number}
   */
  static roundNumber(number, accurate = 4) {
    return Math.round(number * Math.pow(10, accurate)) / Math.pow(10, accurate);
  }

  static showOverlay() {
    store.commit('overlay/show');
  }

  static hideOverlay() {
    store.commit('overlay/hide');
  }

  /**
   *
   * @param customerProfile customer with descendents accessed via CustomerService.readProfileFromAPI(...)
   * @param building building to check
   * @returns {boolean} true: if customer or one of the customer-descendents is owner or operator<br/>
   * false: otherwise
   */
  static checkCustomerWithDescendentsForBuilding(customerProfile, building){
    if (customerProfile.descendants === undefined ) {
      // error: customer not accessed via readProfileFromAPI(...) and does not contain descendents-field
      throw new Error("Parameter customerProfile has no descendants field");
    }
    let descendantIds = customerProfile.descendants.map( (desc) => desc.id);
    let ids = [customerProfile.id, ...descendantIds];
    for (let id of ids) {
      if (building.owner === id) {
        return true;
      }
      if (building.operator === id) {
        return true;
      }
    }
    return false;
  }

}

